import DexfTypeCodeToGlobalTypeMap from '../../services/products/DexfTypeCodeToGlobalTypeMap.json';
import productService, { getProduct } from '../../services/products';
import constants from '../../settings/constants';
import articles, { getArticleContent } from '../../services/products/articles';
import {
  DimensionRemapObject,
  MeasurementsRule,
  MeasurementsSetting,
  MinMaxObject,
  ProductsByIdObject,
} from './productsTypes';
import { TacItem, Itemid, Itemids, IowsArticle } from '../../generalTypes';
import { TacItems } from '../rawData/RawDataTypes';
import { digAllItems } from '../tac/tacHelpers';

/**
 * Returns the global type name based on type code.
 *
 * @param {string} typeCode Type code.
 * @returns {string} Global type name.
 */
export const getGlobalType = (typeCode: string): string | null =>
  // @ts-ignore
  DexfTypeCodeToGlobalTypeMap[typeCode] || null;

/**
 * Returns all products as an object with productID as key
 *
 * @returns {object}
 */
export const getProductsAsObject = (): ProductsByIdObject =>
  productService
    .getAll()
    .reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {});

/**
 * Returns a set of the different dimensions for all products
 * @returns Set
 */
export const getAllDimensionsKeys = () =>
  productService.getAll().reduce((acc, { id }) => {
    const article = articles.getArticle(id);

    if (article?.content?.measure)
      article.content.measure.forEach(({ typeCode }: any) => {
        const globalType = getGlobalType(typeCode);
        globalType && acc.add(globalType.toLowerCase());
      });

    return acc;
  }, new Set());

/**
 * Checks that we have a product and that settings are declared for said product
 *
 * @param product {object}
 * @returns {*}
 */
export const productSettingsExists = (product: TacItem): MeasurementsRule =>
  product && getProductMeasurementSettings(product.filter.type);

/**
 * Derives the specific product settings from the ranges settings file based on product type
 *
 * @param productType {string}
 * @returns {*}
 */
export const getProductMeasurementSettings = (
  productType: string
): MeasurementsRule =>
  constants.MEASUREMENTS_SETTINGS.displayMeasurement.find(
    ({ affects }: MeasurementsSetting) => affects?.includes(productType)
  )?.rules;

/**
 * Retrieves the index of a remap by product ids
 *
 * @param productIds {array}
 * @returns {*}
 */
const getIndexOfRemap = (productIds: Itemids): number =>
  productIds.findIndex((id: Itemid) =>
    constants.MEASUREMENTS_SETTINGS.productsRemap.find(
      ({ affects }: DimensionRemapObject) => affects.includes(id)
    )
  );

/**
 * Returns remap keys for a product if there are any
 *
 * @param productIds {array}
 * @returns {*}
 */
export const getProductMeasurementRemapKeys = (
  productIds: Itemids
): DimensionRemapObject => {
  const index: number = getIndexOfRemap(productIds);

  return index > -1
    ? constants.MEASUREMENTS_SETTINGS.productsRemap[index]
    : false;
};

/**
 * Returns smallest and largest value from an array as an object
 *
 * @param values {array}
 * @returns {{min: number, max: number}}
 */
export const getSmallestAndLargest = (values: number[]): MinMaxObject => ({
  min: Math.min.apply(Math, values),
  max: Math.max.apply(Math, values),
});

/**
 * Used to get parts that are in the
 * iows object but not in the parts object.
 * @example shelf-clothes-rail Elvarli
 */
const getIowsItemsAsIds = (iows: IowsArticle[]) =>
  iows.reduce((acc: string[], curr: IowsArticle) => [...acc, curr.itemno], []);

export const getAllPartsAsIdsFromItem = (item: TacItem): Itemid[] => {
  const { parts, iows } = item;
  // If the original item id is not removed, the recursive function will call itself for ever.
  const removeIowsIdSameAsOriginalItemId = (iowsIds: string[]) =>
    iowsIds.filter(iowsId => iowsId !== item.id);

  const partsArray = parts ? Object.values(parts) : [];
  const iowsArray = iows
    ? removeIowsIdSameAsOriginalItemId(getIowsItemsAsIds(iows))
    : [];
  return [...partsArray, ...iowsArray].reduce((acc: any[], currPart) => {
    const product = getProduct(currPart, true);
    return product && product.parts
      ? [...acc, currPart, ...getAllPartsAsIdsFromItem(product)]
      : [...acc, currPart];
  }, []);
};

export const getAllPartsAsIdsFromItems = (items: TacItems): Itemid[][] => {
  const iterableRawData: TacItem[] = Object.values(items);
  return [
    ...iterableRawData.reduce((acc: Itemid[][], curr: TacItem) => {
      return curr.parts || curr.iows.length
        ? [...acc, getAllPartsAsIdsFromItem(curr)]
        : [...acc];
    }, []),
  ];
};
const removeDuplicates = (array: any[]) => [...new Set(array)];

export const getAllPartsAsProductsFromItem = (item: TacItem): TacItem[] => {
  return removeDuplicates(
    getAllPartsAsIdsFromItem(item).reduce((acc: TacItem[], part: string) => {
      return [...acc, getProduct(part, true)];
    }, [])
  );
};

/**
 * Finds and returns all parts that are also part of the item in its current state.
 * @example shelf-drawer sometimes has brackets and sometimes not depending
 * on whether its attached to side-units or posts.
 * @param parts
 * @returns
 */
export const getAllPartsThatAreAlsoAttachedAsProducts = (item: TacItem) => {
  const allItems = digAllItems([item]);
  const partsIds = getAllPartsAsIdsFromItem(item);
  const iowsIds = getIowsItemsAsIds(item.iows);
  return removeDuplicates(
    [...partsIds, ...iowsIds]
      .filter(id =>
        allItems.find(
          (item: TacItem) =>
            item.id === id ||
            getIowsItemsAsIds(item.iows).some(iowsId => iowsId === id)
        )
      )
      .reduce(
        (acc: TacItem[], curr: string) => [...acc, getProduct(curr, true)],
        []
      )
  );
};

const getProductIowsId = (product: TacItem) => product.iows[0]?.itemno;

export const getCommunicatedItemName = (item: TacItem): string => {
  const itemId = getProductIowsId(item);
  if (!itemId) return '';
  const article = getArticleContent(itemId);

  const capitalizeFirstLetter = (name: string) =>
    name.charAt(0).toUpperCase() + name.slice(1);
  return `${capitalizeFirstLetter(article.typeName)}`;
};

export const getCommunicatedItemImageUrl = (item: TacItem): any => {
  const itemId = getProductIowsId(item);
  if (!itemId) return '';
  const article = getArticleContent(itemId);
  return article.image[0].url || '';
};

export const isCombinedItem = (item: TacItem): boolean =>
  getAllPartsThatAreAlsoAttachedAsProducts(item).length > 1;
